import { CommonMessage } from '@frontend/lang';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { reportBug } from '../index';
import './sentry-error-boundary.css';

const RELOAD_SECONDS = 30;

type ReportState = 'idle' | 'open' | 'submitting' | 'success' | 'error';

export interface KioskErrorFallbackProps {
    error: Error;
    eventId: string;
    resetError: () => void;
}

export function KioskErrorFallback({ error: _error, eventId }: KioskErrorFallbackProps) {
    const intl = useIntl();

    const countRef = useRef(RELOAD_SECONDS);
    const [countdown, setCountdown] = useState(RELOAD_SECONDS);

    const [reportState, setReportState] = useState<ReportState>('idle');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');

    const resetTimer = useCallback(() => {
        countRef.current = RELOAD_SECONDS;
        setCountdown(RELOAD_SECONDS);
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            countRef.current -= 1;
            setCountdown(countRef.current);
            if (countRef.current <= 0) {
                window.location.reload();
            }
        }, 1000);
        return () => clearInterval(id);
    }, []);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        resetTimer();
        setReportState('submitting');
        const result = await reportBug({ message, name, email, associatedEventId: eventId });
        setReportState(result ? 'success' : 'error');
    }

    const progressPercent = (countdown / RELOAD_SECONDS) * 100;

    return (
        <div className='seb-kiosk-overlay'>
            <div className='seb-card'>
                <div className='seb-header'>
                    <h1 className='seb-title'>
                        <FormattedMessage
                            id='SentryErrorBoundary.Title'
                            description='Title shown on the error boundary screen'
                            defaultMessage='Oops!'
                        />
                    </h1>
                </div>

                <div className='seb-body'>
                    <p className='seb-description'>
                        <FormattedMessage
                            id='KioskErrorFallback.Description'
                            description='Body text shown on the kiosk error boundary, explaining the page will auto-reload'
                            defaultMessage='Something went wrong. The page will automatically reload shortly.'
                        />
                    </p>

                    {reportState === 'success' && (
                        <p className='seb-success-msg'>
                            {CommonMessage.ERRORS.BUG_REPORT.FORM_SUCCESS((chunks) => (
                                <strong>{chunks}</strong>
                            ))}
                        </p>
                    )}

                    {(reportState === 'open' || reportState === 'submitting' || reportState === 'error') && (
                        <form
                            onSubmit={handleSubmit}
                            className='seb-form'>
                            <div className='seb-field'>
                                <label
                                    htmlFor='kiosk-fb-name'
                                    className='seb-label'>
                                    {CommonMessage.FORMS.NAME}
                                </label>
                                <input
                                    id='kiosk-fb-name'
                                    type='text'
                                    className='seb-input'
                                    value={name}
                                    onChange={(e) => {
                                        resetTimer();
                                        setName(e.target.value);
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: 'SentryErrorBoundary.Form.NamePlaceholder',
                                        description: 'Placeholder for the name field in the bug report form',
                                        defaultMessage: 'Your name (optional)'
                                    })}
                                    disabled={reportState === 'submitting'}
                                />
                            </div>
                            <div className='seb-field'>
                                <label
                                    htmlFor='kiosk-fb-email'
                                    className='seb-label'>
                                    {CommonMessage.ERRORS.BUG_REPORT.FORM_EMAIL_LABEL}
                                </label>
                                <input
                                    id='kiosk-fb-email'
                                    type='email'
                                    className='seb-input'
                                    value={email}
                                    onChange={(e) => {
                                        resetTimer();
                                        setEmail(e.target.value);
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: 'SentryErrorBoundary.Form.EmailPlaceholder',
                                        description: 'Placeholder for the email field in the bug report form',
                                        defaultMessage: 'your@email.com (optional)'
                                    })}
                                    disabled={reportState === 'submitting'}
                                />
                            </div>
                            <div className='seb-field'>
                                <label
                                    htmlFor='kiosk-fb-message'
                                    className='seb-label'>
                                    {CommonMessage.ERRORS.BUG_REPORT.FORM_MESSAGE_LABEL} <span className='seb-required'>*</span>
                                </label>
                                <textarea
                                    id='kiosk-fb-message'
                                    className='seb-textarea'
                                    rows={3}
                                    value={message}
                                    onChange={(e) => {
                                        resetTimer();
                                        setMessage(e.target.value);
                                    }}
                                    placeholder={intl.formatMessage({
                                        id: 'SentryErrorBoundary.Form.MessagePlaceholder',
                                        description: 'Placeholder for the message field in the bug report form',
                                        defaultMessage: 'Describe what you were doing when the error occurred...'
                                    })}
                                    required
                                    disabled={reportState === 'submitting'}
                                />
                            </div>
                            {reportState === 'error' && <p className='seb-error-msg'>{CommonMessage.ERRORS.BUG_REPORT.FORM_ERROR}</p>}
                            <div className='seb-form-actions'>
                                <button
                                    type='submit'
                                    disabled={reportState === 'submitting' || !message.trim()}
                                    className='seb-btn seb-btn-secondary'>
                                    {reportState === 'submitting' && (
                                        <span
                                            className='seb-spinner-sm'
                                            role='status'
                                        />
                                    )}
                                    {reportState === 'submitting' ? CommonMessage.ERRORS.BUG_REPORT.FORM_SENDING : CommonMessage.ERRORS.BUG_REPORT.FORM_SUBMIT}
                                </button>
                                <button
                                    type='button'
                                    className='seb-btn-link'
                                    onClick={() => {
                                        resetTimer();
                                        setReportState('idle');
                                    }}
                                    disabled={reportState === 'submitting'}>
                                    {CommonMessage.BUTTONS.CANCEL}
                                </button>
                            </div>
                        </form>
                    )}

                    {reportState === 'idle' && (
                        <button
                            type='button'
                            className='seb-btn seb-btn-outline'
                            onClick={() => {
                                resetTimer();
                                setReportState('open');
                            }}>
                            {CommonMessage.ERRORS.BUG_REPORT.BUTTON}
                        </button>
                    )}
                </div>

                <div className='seb-countdown-bar'>
                    <span className='seb-countdown-text'>
                        {intl.formatMessage(
                            {
                                id: 'KioskErrorFallback.ReloadIn',
                                description: 'Countdown text shown at the bottom of the kiosk error screen before auto-reload',
                                defaultMessage: 'Reloading in {seconds}s...'
                            },
                            { seconds: countdown }
                        )}
                    </span>
                    <div className='seb-progress'>
                        <div
                            className='seb-progress-fill'
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
